import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { logAudit } from "@/lib/audit";
import { isDesktopApp, listAvailablePrinters } from "@/lib/printing";
import { buildSampleTicketHtml, printSampleTicket } from "@/lib/sampleInvoice";
import type { ThermalPaper } from "@/lib/thermalInvoice";
import Modal from "@/components/base/Modal";
import { useAuth } from "@/context/AuthContext";
import { useAppSettings } from "@/context/AppSettingsContext";
import { useInvoiceSettings } from "@/hooks/usePosConfig";
import { useToast } from "@/context/ToastContext";
import { TextField, SelectField, ToggleSwitch } from "@/components/base/FormControls";

interface PrinterForm {
  pos_printer: string;
  pos_width: string;
  a4_printer: string;
  auto_print: boolean;
  open_drawer: boolean;
  copies: string;
}

const EMPTY: PrinterForm = {
  pos_printer: "",
  pos_width: "80",
  a4_printer: "",
  auto_print: true,
  open_drawer: false,
  copies: "1",
};

export default function PrintersTab() {
  const toast = useToast();
  const { profile } = useAuth();
  const { settings, fiscal } = useAppSettings();
  const { posSettings } = useInvoiceSettings();
  const [form, setForm] = useState<PrinterForm>(EMPTY);
  const [rowId, setRowId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detected, setDetected] = useState<string[]>([]);
  const [detecting, setDetecting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("printer_settings")
        .select("id, pos_printer, pos_width, a4_printer, auto_print, open_drawer, copies")
        .limit(1)
        .maybeSingle();
      if (error) {
        toast.error("No se pudo cargar la configuración de impresoras.");
        return;
      }
      if (data) {
        setRowId(data.id);
        setForm({
          pos_printer: data.pos_printer ?? "",
          pos_width: String(data.pos_width ?? 80),
          a4_printer: data.a4_printer ?? "",
          auto_print: data.auto_print ?? true,
          open_drawer: data.open_drawer ?? false,
          copies: String(data.copies ?? 1),
        });
      }
    } catch {
      toast.error("No se pudo cargar la configuración de impresoras.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    load();
  }, [load]);

  const update = <K extends keyof PrinterForm>(key: K, value: PrinterForm[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        pos_printer: form.pos_printer.trim() || null,
        pos_width: Number.parseInt(form.pos_width, 10) || 80,
        a4_printer: form.a4_printer.trim() || null,
        auto_print: form.auto_print,
        open_drawer: form.open_drawer,
        copies: Number.parseInt(form.copies, 10) || 1,
        updated_at: new Date().toISOString(),
      };
      const query = rowId
        ? supabase.from("printer_settings").update(payload).eq("id", rowId)
        : supabase.from("printer_settings").insert(payload);
      const { error } = await query;
      if (error) {
        toast.error("No se pudo guardar la configuración de impresión.");
        return;
      }
      await logAudit({
        userId: profile?.id ?? null,
        userName: `${profile?.full_name ?? ""} ${profile?.last_name ?? ""}`.trim() || "Usuario",
        action: "configurar",
        entity: "printer_settings",
        entityId: rowId ?? "printer_settings",
        newValue: { pos_width: payload.pos_width, auto_print: payload.auto_print },
      });
      if (!rowId) await load();
      toast.success("Configuración de impresoras guardada.");
    } catch {
      toast.error("Ocurrió un error inesperado.");
    } finally {
      setSaving(false);
    }
  };

  const desktop = isDesktopApp();

  const paper: ThermalPaper = form.pos_width === "58" ? "pos58" : "pos80";
  // El ticket POS solo muestra datos fiscales si el comprobante está activo
  // Y además el NCF/e-CF está habilitado en el formato POS (Ajustes > Facturas).
  // Es la misma regla que usa la factura real, para que la prueba sea fiel.
  const fiscalEnabled = (fiscal.enabled ?? false) && posSettings?.show_ncf !== false;

  const company = useMemo(
    () => ({
      name: settings.company_name ?? "Sistema RKC",
      rnc: settings.rnc,
      address: settings.address,
      phone: settings.phone,
      whatsapp: settings.whatsapp,
      email: settings.email,
      logoUrl: settings.logo_url,
    }),
    [settings],
  );

  const previewHtml = useMemo(
    () => buildSampleTicketHtml(company, paper, fiscalEnabled),
    [company, paper, fiscalEnabled],
  );

  const handleDetect = async () => {
    setDetecting(true);
    try {
      const list = await listAvailablePrinters();
      setDetected(list);
      if (list.length === 0) {
        toast.info("No se detectaron impresoras. Abre la aplicación de escritorio para detectarlas.");
      } else {
        toast.success(`Se detectaron ${list.length} impresora(s). Tócalas para asignarlas.`);
      }
    } finally {
      setDetecting(false);
    }
  };

  const handleTest = () => {
    printSampleTicket(
      company,
      paper,
      fiscalEnabled,
      {
        printer: form.pos_printer.trim() || null,
        copies: Number.parseInt(form.copies, 10) || 1,
        openDrawer: form.open_drawer,
      },
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center gap-3 rounded-lg border border-background-200 bg-background-100 py-14 text-sm text-foreground-500">
        <i className="ri-loader-4-line animate-spin text-lg"></i>
        Cargando configuración de impresoras...
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <section
        className={`flex items-center gap-3 rounded-lg border p-4 ${
          desktop
            ? "border-accent-300 bg-accent-100"
            : "border-background-200 bg-background-100"
        }`}
      >
        <span
          className={`flex h-9 w-9 items-center justify-center rounded-md ${
            desktop ? "bg-accent-500 text-background-50" : "bg-secondary-200 text-secondary-900"
          }`}
        >
          <i className={`${desktop ? "ri-computer-line" : "ri-global-line"} text-lg`}></i>
        </span>
        <div className="min-w-0">
          <p className="font-heading text-sm font-semibold text-foreground-900">
            {desktop ? "Modo escritorio (impresión directa)" : "Modo navegador"}
          </p>
          <p className="text-xs text-foreground-600">
            {desktop
              ? "La aplicación detectó el entorno de escritorio: las facturas se envían directo a la impresora configurada."
              : "Se usará el diálogo de impresión del navegador. Al abrir la app de escritorio, la impresión será directa y automática."}
          </p>
        </div>
      </section>

      {detected.length > 0 ? (
        <section className="flex flex-col gap-3 rounded-lg border border-accent-300 bg-accent-50 p-5">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-500 text-background-50">
              <i className="ri-radar-line text-base"></i>
            </span>
            <div>
              <h3 className="font-heading text-sm font-semibold text-foreground-900">Impresoras detectadas</h3>
              <p className="text-xs text-foreground-600">Asigna cada impresora a tickets (POS) o a factura A4.</p>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            {detected.map((name) => (
              <div
                key={name}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2"
              >
                <span className="flex min-w-0 items-center gap-2 text-sm text-foreground-800">
                  <i className="ri-printer-line shrink-0 text-base text-foreground-500"></i>
                  <span className="truncate">{name}</span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => update("pos_printer", name)}
                    className={`whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                      form.pos_printer.trim() === name
                        ? "border-primary-400 bg-primary-100 text-primary-800"
                        : "border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    Usar en POS
                  </button>
                  <button
                    type="button"
                    onClick={() => update("a4_printer", name)}
                    className={`whitespace-nowrap rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                      form.a4_printer.trim() === name
                        ? "border-primary-400 bg-primary-100 text-primary-800"
                        : "border-background-300 bg-background-50 text-foreground-700 hover:bg-background-100"
                    }`}
                  >
                    Usar en A4
                  </button>
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <section className="flex flex-col gap-5 rounded-lg border border-background-200 bg-background-100 p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-printer-line text-base"></i>
          </span>
          <h3 className="font-heading text-sm font-semibold text-foreground-900">Impresora de tickets (POS)</h3>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Impresora POS"
            name="pos_printer"
            value={form.pos_printer}
            onChange={(value) => update("pos_printer", value)}
            placeholder="Nombre de la impresora térmica"
            hint="Se usará automáticamente cuando la app corra en el entorno de escritorio."
          />
          <SelectField
            label="Ancho del ticket"
            name="pos_width"
            value={form.pos_width}
            onChange={(value) => update("pos_width", value)}
            options={[
              { value: "58", label: "58 mm" },
              { value: "80", label: "80 mm" },
            ]}
          />
        </div>
      </section>

      <section className="flex flex-col gap-5 rounded-lg border border-background-200 bg-background-100 p-5">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-200 text-secondary-900">
            <i className="ri-file-text-line text-base"></i>
          </span>
          <h3 className="font-heading text-sm font-semibold text-foreground-900">Impresora A4 / PDF</h3>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <TextField
            label="Impresora A4"
            name="a4_printer"
            value={form.a4_printer}
            onChange={(value) => update("a4_printer", value)}
            placeholder="Nombre de la impresora A4"
          />
          <TextField
            label="Copias por defecto"
            name="copies"
            value={form.copies}
            onChange={(value) => update("copies", value.replace(/[^0-9]/g, ""))}
            inputMode="numeric"
            placeholder="1"
          />
        </div>
      </section>

      <section className="flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-5">
        <h3 className="font-heading text-sm font-semibold text-foreground-900">Comportamiento</h3>
        <ToggleSwitch
          label="Impresión automática"
          description="Imprime el ticket automáticamente al confirmar una venta en el POS."
          checked={form.auto_print}
          onChange={(checked) => update("auto_print", checked)}
        />
        <ToggleSwitch
          label="Abrir gaveta de dinero"
          description="Envía la señal de apertura de gaveta al imprimir cuando la impresora lo soporte."
          checked={form.open_drawer}
          onChange={(checked) => update("open_drawer", checked)}
        />
      </section>

      <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={handleDetect}
          disabled={detecting}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100 disabled:opacity-70"
        >
          {detecting ? (
            <i className="ri-loader-4-line animate-spin text-base"></i>
          ) : (
            <i className="ri-radar-line text-base"></i>
          )}
          Detectar impresoras
        </button>
        <button
          type="button"
          onClick={handleTest}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
        >
          <i className="ri-printer-cloud-line text-base"></i>
          Prueba de impresión
        </button>
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:bg-background-100"
        >
          <i className="ri-eye-line text-base"></i>
          Ver factura de prueba
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-70"
        >
          {saving ? (
            <i className="ri-loader-4-line animate-spin text-base"></i>
          ) : (
            <i className="ri-save-3-line text-base"></i>
          )}
          Guardar cambios
        </button>
      </div>

      <Modal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        title="Factura de prueba"
        subtitle={`Formato ${form.pos_width} mm · ${
          fiscalEnabled ? "con facturación electrónica" : "factura comercial normal"
        }`}
        size="lg"
        footer={
          <button
            type="button"
            onClick={handleTest}
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600"
          >
            <i className="ri-printer-line text-base"></i>
            Imprimir esta prueba
          </button>
        }
      >
        <div className="flex flex-col items-center gap-3">
          <p className="text-xs text-foreground-500">
            Esta es la vista real del ticket. En pantalla se muestra a escala; el ancho y la altura
            finales los define la impresora.
          </p>
          <div className="overflow-hidden rounded-lg border border-background-200 bg-white">
            <iframe
              title="Vista previa de factura de prueba"
              srcDoc={previewHtml}
              className={`block h-[70vh] bg-white ${paper === "pos58" ? "w-[260px]" : "w-[340px]"}`}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}